import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { Store, StoreConfig } from 'app/shared/graph';

import { StoreConfigService } from './store-config.service';

@Component({
    selector: 'admin-store-settings',
    templateUrl: './store-settings-page.component.html',
    styleUrls: ['./store-settings-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, FormsModule, MatIcon, MatCheckbox, SaveButtonComponent, ErrorComponent, RouterLink, MatFormFieldModule, MatInputModule, MatIconButton],
})
export class StoreSettingsPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly storeConfigService = inject(StoreConfigService);

    storeId: string;
    storeConfig?: StoreConfig;
    storeWithConfig?: Store;
    trustedExternalPhotoHostInput = '';

    ngOnInit(): void {
        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;
            this.subscribeWithGenericLoadingErrorHandling(this.storeConfigService.getStoreConfig(this.storeId),
                storeWithConfig => {
                    this.storeWithConfig = storeWithConfig;
                    this.storeConfig = { ...storeWithConfig.config };
                });
        }));
    }

    ngOnDestroy(): void {
        super.unsubscribeSubscriptions();
    }

    save() {
        if (!this.storeConfig) {
            return;
        }
        this.trustedExternalPhotoHostInputError = '';
        this.pageStatus = PageStatus.saving;
        this.subscribeWithGenericSavingErrorHandling(this.storeConfigService.updateStoreConfig({
            storeId: this.storeId,
            allowAnonymousRequest: this.storeConfig.allowAnonymousRequest,
            isRecentUploadsEnabled: this.storeConfig.isRecentUploadsEnabled,
            isProofingEnabled: this.storeConfig.isProofingEnabled,
            uploadInBackgroundEnabled: this.storeConfig.uploadInBackgroundEnabled,
            enableThumbnailGeneration: this.storeConfig.enableThumbnailGeneration,
            useSecureRenewToken: this.storeConfig.useSecureRenewToken,
            webhookLogEnabled: this.storeConfig.webhookLogEnabled,
            trustedExternalPhotoHosts: (this.storeConfig.trustedExternalPhotoHosts ?? []).filter(
                (x): x is string => x != null,
            ),
        }));
    }

    addTrustedExternalPhotoHost(): void {
        if (!this.storeConfig) {
            return;
        }

        if (!this.validateTrustedHost(this.trustedExternalPhotoHostInput)) {
            return;
        }

        if (!this.storeConfig.trustedExternalPhotoHosts) {
            this.storeConfig.trustedExternalPhotoHosts = [];
        }

        if (!this.storeConfig.trustedExternalPhotoHosts.includes(this.trustedExternalPhotoHostInput)) {
            this.storeConfig.trustedExternalPhotoHosts.push(this.trustedExternalPhotoHostInput);
        }

        this.trustedExternalPhotoHostInput = '';
    }

    removeTrustedExternalPhotoHost(url: string): void {
        if (!this.storeConfig?.trustedExternalPhotoHosts) {
            return;
        }
        this.storeConfig.trustedExternalPhotoHosts = this.storeConfig.trustedExternalPhotoHosts.filter((x: string) => x !== url);
    }

    trustedExternalPhotoHostInputError = '';

    private validateTrustedHost(input: string): boolean {
        const value = input?.trim();
        if (!value) {
            this.trustedExternalPhotoHostInputError = 'Host is required.';
            return false;
        }

        let url: URL;
        try {
            url = new URL(value);
        } catch {
            this.trustedExternalPhotoHostInputError = 'Enter a valid absolute URL.';
            return false;
        }

        if (url.protocol !== 'https:') {
            this.trustedExternalPhotoHostInputError = 'Only HTTPS URLs are allowed.';
            return false;
        }

        this.trustedExternalPhotoHostInputError = '';
        return true;
    }
}
